// ================================================================================
// ECOMSIMPLY - BLUE-OCEAN TABLE COMPONENT
// Tableau des opportunités Blue-Ocean avec scores propriétaires
// ================================================================================

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Eye, ExternalLink, TrendingUp, TrendingDown, 
  Minus, Star, Zap, Target, BarChart3, Factory 
} from 'lucide-react';
import { Card } from './ui/Card';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';

const BlueOceanTable = ({ opportunities, loading, onOpportunityClick }) => {
  const [sortField, setSortField] = useState('bankability');
  const [sortDirection, setSortDirection] = useState('desc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedOpportunities = React.useMemo(() => {
    return [...opportunities].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortField) {
        case 'ess':
        case 'dgs':
        case 'fmi':
        case 'sri':
        case 'bankability':
          aValue = a.scores?.[sortField] || 0;
          bValue = b.scores?.[sortField] || 0;
          break;
        case 'title':
          aValue = a.title || '';
          bValue = b.title || '';
          break;
        case 'price':
          aValue = a.price_buybox || 0;
          bValue = b.price_buybox || 0;
          break;
        case 'rating':
          aValue = a.rating || 0;
          bValue = b.rating || 0;
          break;
        case 'bsr':
          aValue = a.bsr || 999999;
          bValue = b.bsr || 999999;
          break;
        default:
          return 0;
      }
      
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  }, [opportunities, sortField, sortDirection]);

  const getScoreColor = (score) => {
    if (score >= 85) return 'text-green-600';
    if (score >= 70) return 'text-blue-600';
    if (score >= 55) return 'text-yellow-600';
    return 'text-gray-600';
  };

  const getScoreBadgeColor = (score) => {
    if (score >= 85) return 'bg-green-100 text-green-700 border-green-200';
    if (score >= 70) return 'bg-blue-100 text-blue-700 border-blue-200';
    if (score >= 55) return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    return 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const getTrendIcon = (signal) => {
    switch (signal) {
      case 'bullish': return <TrendingUp className="w-4 h-4 text-green-500" />;
      case 'bearish': return <TrendingDown className="w-4 h-4 text-red-500" />;
      default: return <Minus className="w-4 h-4 text-gray-400" />;
    }
  };

  const formatPrice = (price) => {
    return price ? `${price.toFixed(2)}€` : 'N/A';
  };

  const formatBSR = (bsr) => {
    if (!bsr) return 'N/A';
    return bsr.toLocaleString();
  };

  const SortableHeader = ({ field, children, className = "" }) => (
    <th 
      className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-50 ${className}`}
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortField === field && (
          <span className="text-blue-600">
            {sortDirection === 'asc' ? '↑' : '↓'}
          </span>
        )}
      </div>
    </th>
  );

  if (loading) {
    return (
      <Card className="p-8 text-center">
        <div className="animate-pulse">
          <div className="w-full h-64 bg-gray-200 rounded"></div>
        </div>
        <p className="text-gray-600 mt-4">Chargement des opportunités...</p>
      </Card>
    );
  }

  if (opportunities.length === 0) {
    return (
      <Card className="p-8 text-center">
        <p className="text-gray-600">Aucune opportunité disponible.</p>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-blue-600" />
          Opportunités Blue-Ocean ({opportunities.length})
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Triées par scores propriétaires ESS, DGS, FMI, SRI et Bankability
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <SortableHeader field="title" className="w-64">
                Produit
              </SortableHeader>
              <SortableHeader field="ess">
                <Target className="w-3 h-3" />
                ESS
              </SortableHeader>
              <SortableHeader field="dgs">
                <BarChart3 className="w-3 h-3" />
                DGS
              </SortableHeader>
              <SortableHeader field="fmi">
                <Zap className="w-3 h-3" />
                FMI
              </SortableHeader>
              <SortableHeader field="sri">
                <Factory className="w-3 h-3" />
                SRI
              </SortableHeader>
              <SortableHeader field="bankability">
                <Star className="w-3 h-3" />
                Bankability
              </SortableHeader>
              <SortableHeader field="price">
                Prix
              </SortableHeader>
              <SortableHeader field="rating">
                Rating
              </SortableHeader>
              <SortableHeader field="bsr">
                BSR
              </SortableHeader>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedOpportunities.map((opportunity, index) => (
              <motion.tr
                key={opportunity.asin}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="hover:bg-gray-50 cursor-pointer"
                onClick={() => onOpportunityClick(opportunity)}
              >
                {/* Produit */}
                <td className="px-4 py-4">
                  <div className="flex items-center gap-3">
                    {opportunity.images?.[0] && (
                      <img
                        src={opportunity.images[0]}
                        alt={opportunity.title}
                        className="w-12 h-12 object-cover rounded-lg border"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {opportunity.title}
                      </p>
                      <p className="text-xs text-gray-500">
                        ASIN: {opportunity.asin}
                      </p>
                      <div className="flex items-center gap-1 mt-1">
                        {getTrendIcon(opportunity.scores?.trend_signal)}
                        <span className="text-xs text-gray-500">
                          {opportunity.scores?.trend_signal || 'neutral'}
                        </span>
                      </div>
                    </div>
                  </div>
                </td>

                {/* ESS Score */}
                <td className="px-4 py-4">
                  <Badge className={`${getScoreBadgeColor(opportunity.scores?.ess || 0)} font-mono text-sm`}>
                    {(opportunity.scores?.ess || 0).toFixed(0)}
                  </Badge>
                  <div className="text-xs text-gray-500 mt-1">
                    {opportunity.scores?.ess >= 85 ? 'Excellent' :
                     opportunity.scores?.ess >= 70 ? 'Bon' :
                     opportunity.scores?.ess >= 55 ? 'Moyen' : 'Faible'}
                  </div>
                </td>

                {/* DGS Score */}
                <td className="px-4 py-4">
                  <Badge className={`${getScoreBadgeColor(opportunity.scores?.dgs || 0)} font-mono text-sm`}>
                    {(opportunity.scores?.dgs || 0).toFixed(0)}
                  </Badge>
                  <div className="text-xs text-gray-500 mt-1">
                    {opportunity.scores?.dgs >= 80 ? 'Excellent' :
                     opportunity.scores?.dgs >= 65 ? 'Bon' :
                     opportunity.scores?.dgs >= 45 ? 'Moyen' : 'Faible'}
                  </div>
                </td>

                {/* FMI Score */}
                <td className="px-4 py-4">
                  <Badge className={`${getScoreBadgeColor(opportunity.scores?.fmi || 0)} font-mono text-sm`}>
                    {(opportunity.scores?.fmi || 0).toFixed(0)}
                  </Badge>
                  <div className="text-xs text-gray-500 mt-1">
                    {opportunity.scores?.fmi >= 90 ? 'Excellent' :
                     opportunity.scores?.fmi >= 75 ? 'Bon' :
                     opportunity.scores?.fmi >= 55 ? 'Moyen' : 'Faible'}
                  </div>
                </td>

                {/* SRI Score */}
                <td className="px-4 py-4">
                  <Badge className={`${getScoreBadgeColor(opportunity.scores?.sri || 0)} font-mono text-sm`}>
                    {(opportunity.scores?.sri || 0).toFixed(0)}
                  </Badge>
                  <div className="text-xs text-gray-500 mt-1">
                    {opportunity.suppliers?.length || 0} fournisseurs
                  </div>
                </td>

                {/* Bankability Score */}
                <td className="px-4 py-4">
                  <div className="flex items-center gap-2">
                    <Badge className={`${getScoreBadgeColor(opportunity.scores?.bankability || 0)} font-mono text-lg font-bold`}>
                      {(opportunity.scores?.bankability || 0).toFixed(0)}
                    </Badge>
                    {opportunity.scores?.bankability >= 85 && (
                      <Star className="w-4 h-4 text-yellow-500" />
                    )}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Risk: {(opportunity.scores?.risk_index || 0).toFixed(0)}
                  </div>
                </td>

                {/* Prix */}
                <td className="px-4 py-4">
                  <div className="text-sm font-medium text-gray-900">
                    {formatPrice(opportunity.price_buybox)}
                  </div>
                  <div className="text-xs text-gray-500">
                    {opportunity.offers_count || 0} offres
                  </div>
                </td>

                {/* Rating */}
                <td className="px-4 py-4">
                  <div className="flex items-center">
                    <div className="text-sm font-medium text-gray-900">
                      {opportunity.rating ? opportunity.rating.toFixed(1) : 'N/A'}
                    </div>
                    {opportunity.rating && (
                      <Star className="w-3 h-3 text-yellow-400 ml-1" />
                    )}
                  </div>
                  <div className="text-xs text-gray-500">
                    {opportunity.reviews_count?.toLocaleString() || 0} avis
                  </div>
                </td>

                {/* BSR */}
                <td className="px-4 py-4">
                  <div className="text-sm font-medium text-gray-900">
                    {formatBSR(opportunity.bsr)}
                  </div>
                  <div className="text-xs text-gray-500">
                    Catégorie
                  </div>
                </td>

                {/* Actions */}
                <td className="px-4 py-4">
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpportunityClick(opportunity);
                      }}
                      size="sm"
                      variant="outline"
                      className="flex items-center gap-1"
                    >
                      <Eye className="w-3 h-3" />
                      Détails
                    </Button>
                    
                    {opportunity.asin && (
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(`https://amazon.fr/dp/${opportunity.asin}`, '_blank');
                        }}
                        size="sm"
                        variant="outline"
                        className="flex items-center gap-1"
                      >
                        <ExternalLink className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer avec légende */}
      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
        <div className="flex flex-wrap gap-4 text-xs text-gray-600">
          <div className="flex items-center gap-1">
            <Target className="w-3 h-3" />
            <span><strong>ESS:</strong> Empty-Shelf Score (concurrence vide)</span>
          </div>
          <div className="flex items-center gap-1">
            <BarChart3 className="w-3 h-3" />
            <span><strong>DGS:</strong> Dominance-Gap Score (pas de leader)</span>
          </div>
          <div className="flex items-center gap-1">
            <Zap className="w-3 h-3" />
            <span><strong>FMI:</strong> First-Mover Index (premier entrant)</span>
          </div>
          <div className="flex items-center gap-1">
            <Factory className="w-3 h-3" />
            <span><strong>SRI:</strong> Supplier Readiness Index (sourcing prêt)</span>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default BlueOceanTable;